import { BREAKPOINTS_V3, useMediaQuery } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useInView, useReducedMotion } from 'framer-motion';
import { useRef } from 'react';

import Motion, { type MotionProps } from '@/app/components/Motion';

import AppUIImage, { type AppUIImageProps } from './AppUIImage';
import type { AppUILottieProps } from './AppUILottie';
import AppUILottie from './AppUILottie';
import SectionContentAppUIBackdrop, {
  type SectionContentAppUIBackdropProps,
} from './SectionContentAppUIBackdrop';

interface AppUIAsset {
  readonly id: string;
  readonly imageData?: AppUIImageProps;
  readonly lottieData?: AppUILottieProps;
}

export interface SectionContentAppUIProps {
  readonly activeIndex: number;
  readonly assets: AppUIAsset[];
  readonly backdrop: SectionContentAppUIBackdropProps;
  readonly index: number;
  readonly shouldLoop?: boolean;
  readonly wrapperClasses?: string;
}

export const getTranslateMotionProps = ({
  isActive,
  isLargeBreakpoint,
  reducedMotion,
}: {
  isActive: boolean;
  isLargeBreakpoint: boolean;
  reducedMotion: boolean;
}): MotionProps => {
  return isLargeBreakpoint
    ? {
        animate: {
          y: 0,
          transitionEnd: {
            y: reducedMotion || isActive ? 0 : 50,
          },
        },
        initial: {
          y: reducedMotion ? 0 : 50,
        },
      }
    : {
        initial: {
          y: 0,
        },
      };
};

export const getFadeMotionProps = ({
  isActive,
  isLargeBreakpoint,
}: {
  isActive: boolean;
  isLargeBreakpoint: boolean;
}): MotionProps => {
  return isLargeBreakpoint
    ? {
        animate: {
          opacity: isActive ? 1 : 0,
          transitionEnd: {
            opacity: isActive ? 1 : 0,
          },
        },
        initial: {
          opacity: 0,
        },
      }
    : {
        initial: {
          opacity: 0,
          y: 0,
        },
        viewport: {
          once: true,
        },
        whileInView: {
          opacity: 1,
        },
      };
};

const SectionContentAppUI = ({
  activeIndex,
  assets,
  backdrop,
  index,
  wrapperClasses,
}: SectionContentAppUIProps): JSX.Element => {
  const isActive = activeIndex === index;
  const ref = useRef<HTMLDivElement>(null);
  const isLargeBreakpoint = useMediaQuery(
    `(min-width: ${BREAKPOINTS_V3.large}px)`,
  );
  const isInView = useInView(ref, {
    once: !isLargeBreakpoint,
  });
  const reducedMotion = useReducedMotion();
  const translateMotionProps = getTranslateMotionProps({
    isActive,
    isLargeBreakpoint: Boolean(isLargeBreakpoint),
    reducedMotion: Boolean(reducedMotion),
  });
  const fadeMotionProps = getFadeMotionProps({
    isActive,
    isLargeBreakpoint: Boolean(isLargeBreakpoint),
  });
  const shouldPlay =
    (Boolean(isLargeBreakpoint) && isActive) ||
    (!isLargeBreakpoint && isInView);

  return (
    <div
      className={cx(
        'pointer-events-none absolute left-0 top-0 z-20 h-[500px] w-full lg:relative lg:left-auto lg:top-auto lg:mt-6 lg:size-auto',
        {
          ...(!!wrapperClasses && { [wrapperClasses]: true }),
        },
      )}
      data-cy="timeline_section_content_app_ui"
      ref={ref}
    >
      <div className="pointer-events-auto absolute bottom-0 left-1/2 z-20 flex -translate-x-1/2 translate-y-14 flex-col gap-y-1 lg:static lg:translate-x-0 lg:translate-y-0">
        {assets.map(({ id, imageData, lottieData }) => (
          <div key={id}>
            <Motion
              {...translateMotionProps}
              className="relative w-[300px] lg:max-w-[30vh]"
              transition={{ duration: 1 }}
            >
              <SectionContentAppUIBackdrop
                {...backdrop}
                isActive={Boolean(isActive)}
                isLargeBreakpoint={Boolean(isLargeBreakpoint)}
              />
              <Motion
                {...fadeMotionProps}
                className="relative z-20"
                key={id}
                transition={{ duration: 1 }}
              >
                {imageData && <AppUIImage key={`${id}_image`} {...imageData} />}
                {lottieData && (
                  <AppUILottie
                    key={`${id}_lottie`}
                    {...lottieData}
                    shouldPlay={shouldPlay}
                  />
                )}
              </Motion>
            </Motion>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SectionContentAppUI;
